import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import ActionsContext from "common/ActionsContext";
import BqnContext from "common/BqnContext";
import Table from "common/graphs/Table/Table";
import { TickOrCross } from "common/graphs/Table/TableRenders";

const TableContainer = styled.div`
  td i.material-icons {
    font-size: 18px;
  }
  button[title="Enable"] {
    i.material-icons {
      color: var(--signal-green) !important;
    }
  }
  button[title="Disable"] {
    i.material-icons {
      color: var(--signal-red) !important;
    }
  }
`;

const InterFaceWrapper = styled.div`
  display: flex;
  align-items: center;

  i.material-icons {
    padding: 0 5px 0 5px;
  }
`;

const CaptureWrapper = styled.div`
  margin-left: 20px;
`;

const doesNothing = () => {};

const notDeleted = ({ deleted = false }) => deleted !== true;

const getFilteredDataWires = (wires, bqnId) => {
  if (bqnId === "all") {
    return wires;
  }
  return wires.filter(({ bqn }) => parseFloat(bqn) === parseFloat(bqnId));
};

const InterfaceName = ({ type, onClick, name }) => {
  if (type === "n/a") {
    return <div>{name}</div>;
  }
  return (
    <div
      className="hyperlink-text"
      onClick={() => onClick("details", { name, type: "ethernet" })}
    >
      {name}
    </div>
  );
};

const CaptureCell = ({
  name,
  type = "ethernet",
  onClick = doesNothing,
  bqnId,
  enabled,
}) => {
  const disabled = enabled !== true || type === "n/a";
  return (
    <CaptureWrapper
      className={`action ${disabled ? "disabled faded" : ""}`}
      title="Capture"
      onClick={() => {
        if (disabled) {
          return;
        }

        onClick("capture", { name, type, bqnId });
      }}
    >
      <i className="material-icons">search</i>
    </CaptureWrapper>
  );
};

const EnableDisableCell = ({
  name,
  management,
  enabled = false,
  onClick = doesNothing,
  type,
}) => {
  return (
    <>
      <div
        className={`action ${
          enabled === true || type === "n/a" ? "disabled faded" : ""
        }`}
        title="Enable"
        onClick={() =>
          enabled !== true &&
          type !== "n/a" &&
          onClick("update", { enable: true, name })
        }
      >
        <i className="material-icons col-green">arrow_upward</i>
      </div>
      <div
        className={`action ${
          enabled !== true || type === "n/a" ? "disabled faded" : ""
        }`}
        title="Disable"
        onClick={() =>
          enabled === true &&
          type !== "n/a" &&
          onClick("update", { disable: true, name })
        }
      >
        <i className="material-icons col-red">arrow_downward</i>
      </div>
    </>
  );
};

const InterfaceCell = ({
  type,
  connected,
  enabled,
  pcap,
  iface,
  management,
  onClick,
  bqnId,
}) => {
  return (
    <InterFaceWrapper>
      <InterfaceName type={type} onClick={onClick} name={iface} />
      <CaptureCell
        type={type}
        enabled={enabled}
        onClick={onClick}
        name={iface}
        bqnId={bqnId}
      />
      <EnableDisableCell
        name={iface}
        type={type}
        enabled={enabled}
        management={management}
        onClick={onClick}
      />
    </InterFaceWrapper>
  );
};

const getColumns = ({ mode, onActionClick, bqnId, doDelete, doReverse }) => [
    ...(mode !== "standalone"
      ? [
          {
            label: "BQN",
            id: "bqn",
            numeric: true,
          },
        ]
      : []),
    {
      label: "ACCESS INTERFACE",
      id: "acc",
      numeric: false,
      renderCell: (value, row) => (
        <InterfaceCell {...value} onClick={onActionClick} bqnId={bqnId === 'all' ? row.bqn: bqnId } />
      ),
      parse: (value) =>  value.iface
    },
    {
      label: "UP",
      id: "accEnabled",
      numeric: false,
      renderCell: (value) => <TickOrCross ok={value} />,
      parse: (value) => value ? "yes" : "no"
    },
    {
      label: "LINK",
      id: "accConnected",
      numeric: false,
      renderCell: (value) => <TickOrCross ok={value} />,
      parse: (value) => value ? "yes" : "no"
    },
    {
      label: "PCAP",
      id: "accPcap",
      numeric: false,
      renderCell: (value) =>  value ? "yes" : "no",
      parse: (value) => value ? "yes" : "no"
    },
    {
      label: "INTERNET INTERFACE",
      id: "inet",
      numeric: false,
      renderCell: (value, row) => (
        <InterfaceCell {...value} onClick={onActionClick} bqnId={bqnId === 'all' ? row.bqn: bqnId }/>
      ),
      parse: (value) =>  value.iface
    },
    {
      label: "UP",
      id: "inetEnabled",
      numeric: false,
      renderCell: (value) => <TickOrCross ok={value} />,
      parse: (value) => value ? "yes" : "no"
    },
    {
      label: "LINK",
      id: "inetConnected",
      numeric: false,
      renderCell: (value) => <TickOrCross ok={value} />,
      parse: (value) => value ? "yes" : "no"
    },
    {
      label: "PCAP",
      id: "inetPcap",
      numeric: false,
      renderCell: (value) =>  value ? "yes" : "no",
      parse: (value) => value ? "yes" : "no"
    },
    {
      label: "ACTIONS",
      id: "ACTIONS",
      actions: [
        {
          label: "Delete",
          icon: "delete",
          onClick: (row) => doDelete([row.__id]),
        },
        {
          label: "Reverse",
          icon: "swap_horiz",
          onClick: (row) => doReverse(row.__id),
        },
      ],
    },
];

const ListWiresWithActions = ({
  items = [],
  onInterfaceClick,
  doDelete,
  doReverse,
}) => {
  const {
    bqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);
  const actions = useContext(ActionsContext);
  const [selected, setSelected] = useState([]);
  const filteredItems = getFilteredDataWires(items, bqnId);

  useEffect(() => setSelected([]), [bqnId]);

  useEffect(() => {
    actions.send("selection-changed", { count: selected.length });
  }, [selected.length]);
  const columns = getColumns({
    mode,
    onActionClick: onInterfaceClick,
    bqnId,
    doDelete,
    doReverse,
  });
  const tableItems = filteredItems.filter(notDeleted).map((item, index) => {
    const {
      type: accType,
      connected: accConnected,
      enabled: accEnabled,
      pcap: accPcap,
      iface: accIface,
    } = item.acc;
    const {
      type: inetType,
      connected: inetConnected,
      enabled: inetEnabled,
      pcap: inetPcap,
      iface: inetIface,
    } = item.inet;
    return {
      ...item,
      id: item.__id !== undefined ? item.__id : index,
      accType,
      accConnected,
      accEnabled,
      accPcap,
      accIface,
      inetType,
      inetConnected,
      inetEnabled,
      inetPcap,
      inetIface,
    };
  });
  return (
    <TableContainer>
      <Table
        columns={columns}
        data={tableItems}
        exportCSV={false}
        isSelectable={true}
        isRowSelectable={false}
        rowsPerPageNr={50}
        selected={selected}
        setSelected={setSelected}
        deleteSelected={(values) => {
          doDelete(values);
          setSelected([]);
        }}
      />
    </TableContainer>
  );
};

export default ListWiresWithActions;
